// js/accounts/sync-router.js
// Runtime dispatcher. Existing call sites do `import { syncManager } from
// '.../pocketbase.js'` — the rest of the codebase has 50+ such usages.
// This proxy keeps that exact shape but routes each call to the backend
// matching the active session (Appwrite -> PocketBase, Nostr -> relays).

import { currentSession } from './session.js';
import { syncManager as pocketbaseSync } from './pocketbase.js';

let _nostrSyncProvider = null;

export function registerNostrSyncProvider(provider) {
    _nostrSyncProvider = provider;
}

const pickBackend = () => {
    const session = currentSession();
    if (session.kind === 'nostr') {
        if (_nostrSyncProvider) {
            try {
                return _nostrSyncProvider() ?? pocketbaseSync;
            } catch (e) {
                console.error('[SYNC-ROUTER] nostr provider threw:', e);
                return pocketbaseSync;
            }
        }
        // Nostr session active but the sync module hasn't loaded yet —
        // fall through to a no-op-friendly stub instead of accidentally
        // writing to PocketBase under a Nostr identity.
        return _nostrPendingStub;
    }
    return pocketbaseSync;
};

// While the Nostr sync module is loading, calls return safely (no-op for
// writes, empty object for reads) so we never accidentally hit PocketBase
// with a Nostr account active.
const _nostrPendingStub = new Proxy(
    {},
    {
        get() {
            return async () => undefined;
        },
    }
);

export const syncManager = new Proxy(
    {},
    {
        get(_target, prop) {
            const backend = pickBackend();
            const value = backend[prop];
            if (typeof value === 'function') return value.bind(backend);
            return value;
        },
    }
);

// Convenience accessor for code that needs to know which backend is live.
export const activeSyncKind = () => currentSession().kind;
